import api from "@/lib/api";
import {
  setLocalAuthIndicator,
  clearLocalAuthIndicator,
  hasLocalAuthIndicator,
  debugLog,
  diagnoseAuthIssues,
} from "@/lib/cookieUtils";
import {
  LoginRequest,
  LoginResponse,
  ChangePasswordRequest,
  JwtResponse,
  ApiMessageDto,
} from "@/types/api";

export const authService = {
  // Login
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      debugLog("Tentando login para:", credentials.email);

      const response = await api.post<LoginResponse>(
        "/auth/login",
        credentials
      );

      // Com cookies HttpOnly, o backend já define os cookies automaticamente
      // Apenas marcar como autenticado no localStorage para controle local
      setLocalAuthIndicator();

      debugLog("Login realizado com sucesso para:", credentials.email);

      return response.data;
    } catch (error) {
      debugLog("Erro no login:", error);
      clearLocalAuthIndicator();
      throw error;
    }
  },

  // Login com token (verificar se ainda está válido)
  // NOTA: Este endpoint usa Authorization header, não cookie
  async loginWithToken(
    token: string
  ): Promise<{ user: any; newToken?: string }> {
    try {
      debugLog("Tentando login com token");

      const response = await api.get("/auth/login-in-with-token", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Verifica se há um novo token no header
      const newToken = response.headers["new-access-token"];
      if (newToken) {
        debugLog("Novo token recebido via header");
        setLocalAuthIndicator();
      }

      return {
        user: response.data,
        newToken,
      };
    } catch (error) {
      debugLog("Falha no login com token:", error);
      this.clearAuthState();
      throw error;
    }
  },

  // Logout
  async logout(): Promise<ApiMessageDto> {
    try {
      debugLog("Fazendo logout no servidor");

      const response = await api.post<ApiMessageDto>("/auth/logout");

      debugLog("Logout realizado no servidor com sucesso");
      return response.data;
    } catch (error) {
      debugLog("Erro no logout do servidor:", error);
      // Continuar com limpeza local mesmo se o servidor falhar
      return { message: "Logout realizado localmente" };
    } finally {
      // Sempre limpa o estado local
      this.clearAuthState();
    }
  },

  // Refresh token (automático via cookies HttpOnly)
  async refreshToken(): Promise<void> {
    try {
      debugLog("Fazendo refresh token...");

      // O backend vai definir novos cookies automaticamente
      await api.post("/auth/refresh");

      debugLog("Refresh token bem-sucedido");
      setLocalAuthIndicator();
    } catch (error) {
      debugLog("Falha no refresh token:", error);
      this.clearAuthState();
      throw error;
    }
  },

  // Obter informações do usuário atual (usa cookie HttpOnly automaticamente)
  async getCurrentUser(): Promise<JwtResponse> {
    try {
      debugLog("Obtendo informações do usuário atual");

      const response = await api.get<JwtResponse>("/auth/me");

      debugLog("Informações do usuário obtidas:", response.data.email);
      return response.data;
    } catch (error) {
      debugLog("Erro ao obter usuário atual:", error);

      // Se falhar ao obter usuário, pode ser que o token esteja inválido
      if ((error as any)?.response?.status === 401) {
        this.clearAuthState();
      }
      throw error;
    }
  },

  // Alterar senha (usa cookie HttpOnly automaticamente)
  async changePassword(
    passwordData: ChangePasswordRequest
  ): Promise<ApiMessageDto> {
    try {
      debugLog("Alterando senha do usuário");

      const response = await api.post<ApiMessageDto>(
        "/auth/change-password",
        passwordData
      );

      debugLog("Senha alterada com sucesso");
      return response.data;
    } catch (error) {
      debugLog("Erro ao alterar senha:", error);
      throw error;
    }
  },

  // Verifica se o usuário está autenticado (baseado no estado local)
  isAuthenticated(): boolean {
    const isAuth = hasLocalAuthIndicator();
    debugLog("Verificando autenticação local:", isAuth);
    return isAuth;
  },

  // Verifica se o token está válido fazendo uma requisição ao servidor
  async validateToken(): Promise<boolean> {
    try {
      debugLog("Validando token no servidor");

      await this.getCurrentUser();

      debugLog("Token válido no servidor");
      return true;
    } catch (error) {
      debugLog("Token inválido no servidor");
      return false;
    }
  },

  // Limpa o estado de autenticação
  clearAuthState(): void {
    debugLog("Limpando estado de autenticação");
    clearLocalAuthIndicator();
    // Com cookies HttpOnly, não podemos limpar os cookies via JavaScript
    // O backend deve lidar com isso no endpoint de logout
  },

  // Função para inicializar autenticação na inicialização da app
  async initializeAuth(): Promise<boolean> {
    debugLog("Inicializando autenticação");

    // Diagnosticar possíveis problemas
    const issues = diagnoseAuthIssues();
    if (issues.length > 0) {
      debugLog("Problemas detectados:", issues);
    }

    const hasToken = this.isAuthenticated();

    if (!hasToken) {
      debugLog("Nenhum indicador de token encontrado");
      return false;
    }

    try {
      debugLog("Verificando validade do token...");

      // Tentar obter dados do usuário para verificar se o token é válido
      await this.getCurrentUser();

      debugLog("Token válido - autenticação inicializada");
      return true;
    } catch (error) {
      debugLog("Token inválido, tentando refresh...");

      try {
        // Tentar fazer refresh
        await this.refreshToken();
        debugLog("Refresh bem-sucedido - autenticação inicializada");
        return true;
      } catch (refreshError) {
        debugLog("Falha no refresh - limpando estado");
        this.clearAuthState();
        return false;
      }
    }
  },

  // Função utilitária para obter informações do token sem validação
  // (útil para casos onde queremos apenas verificar se existe)
  async getTokenInfo(): Promise<JwtResponse | null> {
    try {
      return await this.getCurrentUser();
    } catch (error) {
      return null;
    }
  },

  // Função para diagnóstico de problemas
  async diagnose(): Promise<{
    localAuth: boolean;
    serverAuth: boolean;
    issues: string[];
    userInfo?: JwtResponse;
  }> {
    const localAuth = this.isAuthenticated();
    let serverAuth = false;
    let userInfo: JwtResponse | undefined;

    try {
      userInfo = await this.getCurrentUser();
      serverAuth = true;
    } catch (error) {
      serverAuth = false;
    }

    const issues = diagnoseAuthIssues();

    return {
      localAuth,
      serverAuth,
      issues,
      userInfo,
    };
  },
};
